import { ReactNode, useState } from 'react';
import cx from 'classnames';

import { BottomSheet } from 'components';

import styles from './ConfirmPanel.module.scss';

export type ConfirmPanelProps = {
  show?: boolean;
  name: string;
  title: string;
  message?: string | ReactNode;
  cancelText?: string;
  submitText?: string;
  onCancel?(name: string): void | Promise<void>;
  onSubmit?(name: string): void | Promise<void>;
};

const ConfirmPanel = ({
  show = false,
  name,
  title,
  message,
  cancelText = 'Cancel',
  submitText = 'Submit',
  onCancel,
  onSubmit
}: ConfirmPanelProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleOnCancel = async () => {
    setIsCancelling(true);

    try {
      await onCancel?.(name);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOnSubmit = async () => {
    setIsSubmitting(true);

    try {
      await onSubmit?.(name);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BottomSheet
      blockBodyScroll={false}
      withBackdrop
      closeOnClickBackdrop={false}
      title={title}
      show={show}
      unmountOnExit
      renderHeader={({ headerClasses, titleClasses }) => (
        <div className={headerClasses}>
          <div className={titleClasses}>{title}</div>
        </div>
      )}
    >
      <div className={styles.container}>
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={cx(styles.action, styles.cancel)}
            disabled={isCancelling}
            onClick={handleOnCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            className={cx(styles.action, styles.submit)}
            onClick={handleOnSubmit}
          >
            {submitText}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ConfirmPanel;
